import { Badge } from '@/components/ui/badge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import {
    FormSection,
    MasterDataForm,
} from '../components/master-data/MasterDataForm';
import {
    FilterConfig,
    MasterDataTable,
    TableColumn,
} from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { EdiBagNo } from '../types/ediBagNo';

const EdiBagNoManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEdiBagNo, setEditingEdiBagNo] = useState<EdiBagNo | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>(
    {}
  );
  const [sortConfig, setSortConfig] = useState<{
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }>({});

  // Fetch EDI Bag Numbers with server-side filtering and pagination
  const {
    data: ediBagNosResponse,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      'ediBagNos',
      currentPage,
      pageSize,
      searchTerm,
      currentFilters,
      sortConfig,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(
        `/api/master-data/ediBagNo?${params.toString()}`
      );
      const result = response.data.data || {};
      console.log('EdiBagNo API response:', result);

      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1,
        },
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const ediBagNos = ediBagNosResponse?.data || [];
  const pagination = ediBagNosResponse?.pagination;

  // Create mutation
  const createEdiBagNoMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/ediBagNo', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ediBagNos'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'ediBagNo'],
      });
      toast({
        title: 'Success',
        description: 'EDI Bag No created successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to create EDI Bag No';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateEdiBagNoMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(
        `/api/master-data/ediBagNo/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ediBagNos'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'ediBagNo'],
      });
      toast({
        title: 'Success',
        description: 'EDI Bag No updated successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setEditingEdiBagNo(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to update EDI Bag No';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Delete mutation (hard delete)
  const deleteEdiBagNoMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `/api/master-data/ediBagNo/${id}?soft=false`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ediBagNos'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'ediBagNo'],
      });
      toast({
        title: 'Success',
        description: 'EDI Bag No deleted successfully',
        variant: 'default',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message || 'Failed to delete EDI Bag No',
        variant: 'destructive',
      });
    },
  });

  // Table columns configuration
  const columns: TableColumn<EdiBagNo>[] = [
    {
      key: 'masterEdiBagNo',
      label: 'Master EDI Bag No.',
      sortable: true,
      render: (_, ediBagNo) => (
        <div className="font-medium">{ediBagNo.masterEdiBagNo}</div>
      ),
    },
    {
      key: 'bagNoRange',
      label: 'Bag No Range',
      sortable: true,
      render: (_, ediBagNo) => (
        <div className="font-mono text-sm bg-muted px-2 py-1 rounded">
          {ediBagNo.bagNoRange}
        </div>
      ),
    },
    {
      key: 'remarks',
      label: 'Remarks',
      sortable: false,
      hideOnMobile: true,
      render: (_, ediBagNo) => (
        <div className="text-sm text-muted-foreground max-w-xs truncate">
          {ediBagNo.remarks || '-'}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_, ediBagNo) => (
        <Badge
          variant={ediBagNo.status === 'Active' ? 'default' : 'secondary'}
          className={
            ediBagNo.status === 'Active'
              ? 'bg-green-500 hover:bg-green-600'
              : ''
          }
        >
          {ediBagNo.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hideOnTablet: true,
      render: (_, ediBagNo) => (
        <div className='text-sm text-muted-foreground'>
          {new Date(ediBagNo.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
      ),
    },
  ];

  // Filter configuration
  const enhancedFilterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ],
      placeholder: 'Filter by status',
    },
  ];

  // Form sections configuration
  const formSections: FormSection[] = [
    {
      title: 'EDI Bag No Information',
      description: 'Configure EDI Bag Number details.',
      fields: [
        {
          name: 'masterEdiBagNo',
          label: 'Master EDI Bag No.',
          type: 'text',
          required: true,
          placeholder: 'Enter master EDI bag number',
          description: 'A unique number for the bag',
          validation: z
            .string()
            .min(1, 'Master EDI Bag No is required')
            .max(100, 'Master EDI Bag No cannot exceed 100 characters'),
        },
        {
          name: 'bagNoRange',
          label: 'Bag No Range',
          type: 'text',
          required: true,
          placeholder: 'Enter bag number range (e.g., "1-100", "A001-A500")',
          description: 'A unique range for the bag number',
          validation: z
            .string()
            .min(1, 'Bag No Range is required')
            .max(200, 'Bag No Range cannot exceed 200 characters'),
        },
        {
          name: 'remarks',
          label: 'Remarks',
          type: 'textarea',
          required: false,
          placeholder: 'Enter any remarks or notes (optional)',
          description: 'Additional remarks if any',
          validation: z
            .string()
            .max(500, 'Remarks cannot exceed 500 characters')
            .optional()
            .or(z.literal('')),
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: [
            { value: 'Active', label: 'Active - Available for use' },
            { value: 'Inactive', label: 'Inactive - Not available for use' },
          ],
          description: 'Current status of this EDI Bag No',
        },
      ],
    },
  ];

  // Event handlers
  const handleCreate = () => {
    setEditingEdiBagNo(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (ediBagNo: EdiBagNo) => {
    setEditingEdiBagNo(ediBagNo);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = async (ediBagNo: EdiBagNo) => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete EDI Bag No "${ediBagNo.masterEdiBagNo}"? This action cannot be undone.`
      )
    ) {
      deleteEdiBagNoMutation.mutate(ediBagNo._id);
    }
  };

  const handleSubmit = async (data: any) => {
    if (editingEdiBagNo) {
      updateEdiBagNoMutation.mutate({
        id: editingEdiBagNo._id,
        data,
      });
    } else {
      createEdiBagNoMutation.mutate(data);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingEdiBagNo(null);
    setServerErrors({});
  };

  const handleRefresh = () => {
    refetch();
    toast({
      title: 'Refreshed',
      description: 'EDI Bag No data has been refreshed',
      variant: 'default',
    });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();

      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(
        `/api/master-data/ediBagNo/export?${params.toString()}`
      );

      if (response.data.success) {
        const ediBagNos = response.data.data;

        const exportData = ediBagNos.map((item: EdiBagNo) => ({
          'Master EDI Bag No.': item.masterEdiBagNo,
          'Bag No Range': item.bagNoRange,
          Remarks: item.remarks || '',
          Status: item.status,
          'Created At': new Date(item.createdAt).toLocaleString(),
          'Updated At': new Date(item.updatedAt).toLocaleString(),
        }));

        const headers = Object.keys(exportData[0]);
        const csvContent = [
          headers.join(','),
          ...exportData.map((row: any) =>
            headers
              .map((header) => {
                const value = row[header];
                if (
                  typeof value === 'string' &&
                  (value.includes(',') || value.includes('"'))
                ) {
                  return `"${value.replace(/"/g, '""')}"`;
                }
                return value;
              })
              .join(',')
          ),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `edi-bag-nos-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast({
          title: 'Success',
          description: `Exported ${ediBagNos.length} EDI Bag Numbers`,
          variant: 'default',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Export Failed',
        description:
          error.response?.data?.error?.message || 'Failed to export EDI Bag Numbers',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>
            EDI Bag No Management
          </h1>
          <p className='text-muted-foreground'>
            Manage EDI Bag Numbers and their ranges
          </p>
        </div>
      </div>

      <MasterDataTable<EdiBagNo>
        title='EDI Bag Numbers'
        description='Manage and configure EDI Bag Number information'
        data={ediBagNos}
        columns={columns}
        isLoading={isLoading}
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        currentSearch={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder='Search EDI Bag Numbers...'
        currentFilters={currentFilters}
        onFiltersChange={setCurrentFilters}
        filters={enhancedFilterConfig}
        currentSort={sortConfig}
        onSortChange={setSortConfig}
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        allowImportExport={true}
      />

      <MasterDataForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        title={editingEdiBagNo ? 'Edit EDI Bag No' : 'Create EDI Bag No'}
        sections={formSections}
        initialData={editingEdiBagNo}
        onSubmit={handleSubmit}
        isLoading={
          createEdiBagNoMutation.isPending || updateEdiBagNoMutation.isPending
        }
        mode={editingEdiBagNo ? 'edit' : 'create'}
        serverErrors={serverErrors}
      />
    </div>
  );
};

export default EdiBagNoManagement;


